import { useState } from 'react';
import MethodForm from './MethodForm';
import MethodBody from './MethodBody';
import { deleteMethod } from '../../lib/labData';
import { useConfirm } from '../ConfirmDialog';

// The methods library. A method is written once and attached to as many
// formulas as make it the same way.
export default function MethodsLibrary({ methods, productTypes, formulas, onChanged }) {
  const confirm = useConfirm();
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [error, setError] = useState(null);

  // Which formulas point at each method — shown on the row, and what stops
  // a method being deleted out from under a formula that uses it.
  const usedBy = (methodId) =>
    (formulas || []).filter(
      (f) => f.method_id === methodId || f.product_type?.default_method_id === methodId
    );

  const handleDelete = async (method) => {
    const users = usedBy(method.id);
    if (users.length) {
      setError(
        `${method.name} is used by ${users.map((f) => f.name).join(', ')}. Detach it there first.`
      );
      return;
    }
    if (
      !(await confirm({
        title: `Delete ${method.name}?`,
        body: 'The steps and temperatures go with it. Batches already made keep their own frozen copy, so their sheets are unaffected.',
      }))
    )
      return;
    await deleteMethod(method.id);
    await onChanged();
  };

  return (
    <>
      <div className="lab-toolbar">
        <p className="lab-toolbar-note">
          Written once, attached to any formula made the same way.
        </p>
        <button className="primary-action" onClick={() => setAdding(true)}>
          + New method
        </button>
      </div>

      {error && <p className="formula-error">{error}</p>}

      {(methods || []).length === 0 ? (
        <div className="lab-empty">
          <p>
            No methods yet. Write the steps once and every batch sheet from here on carries them.
          </p>
          <button className="primary-action" onClick={() => setAdding(true)}>
            + New method
          </button>
        </div>
      ) : (
        <ul className="lab-rows">
          {methods.map((method) => {
            const users = usedBy(method.id);
            const open = openId === method.id;
            return (
              <li className="lab-row lab-row-block" key={method.id}>
                <div className="lab-row-line">
                  <button
                    className="lab-row-main"
                    onClick={() => setOpenId(open ? null : method.id)}
                    aria-expanded={open}
                  >
                    <span className="lab-row-name">{method.name}</span>
                    {method.product_type?.name && (
                      <span className="lab-row-variants">{method.product_type.name}</span>
                    )}
                    <span className="lab-row-meta">
                      {(method.steps || []).length} step
                      {(method.steps || []).length === 1 ? '' : 's'}
                    </span>
                    <span className="lab-row-meta">
                      {users.length ? `Used by ${users.length}` : 'Not attached'}
                    </span>
                  </button>
                  <div className="lab-row-actions">
                    <button className="link-button" onClick={() => setEditing(method)}>
                      Edit
                    </button>
                    <button className="link-button" onClick={() => handleDelete(method)}>
                      Delete
                    </button>
                  </div>
                </div>
                {open && (
                  <div className="lab-row-body">
                    <MethodBody method={method} />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {(adding || editing) && (
        <MethodForm
          method={editing}
          productTypes={productTypes}
          onClose={() => {
            setAdding(false);
            setEditing(null);
          }}
          onSaved={onChanged}
        />
      )}
    </>
  );
}
